import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { bonusService } from "../services/api";
import { useAuth } from "./authContext";

const BonusContext = createContext(null);

/** Evita pedir /bonuses/mine/ en cada cambio de foco. */
const FOCUS_REFRESH_DEBOUNCE_MS = 60000;
/** Sin línea base: se consideran "nuevos" los bonos emitidos en los últimos minutos. */
const RECENT_BONUS_MS = 15 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Bonos del usuario (GET /bonuses/mine/) compartidos por el header (insignia),
 * el carrito (pagar con bono) y el perfil (Mis bonos). También detecta el bono
 * de bienvenida tras confirmarse una recarga para mostrar la celebración.
 */
export const BonusProvider = ({ children }) => {
  const { isAuthenticated, user } = useAuth();
  const [available, setAvailable] = useState([]);
  const [history, setHistory] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [celebration, setCelebration] = useState(null);
  const knownIdsRef = useRef(null);
  const lastFetchAt = useRef(0);
  const genRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) return [];
    const gen = ++genRef.current;
    setLoading(true);
    const res = await bonusService.mine();
    if (gen !== genRef.current) return res.available;
    setLoading(false);
    if (!res.ok) return null;
    lastFetchAt.current = Date.now();
    setAvailable(res.available);
    setHistory(res.history);
    setLoaded(true);
    const ids = knownIdsRef.current || new Set();
    res.available.forEach((b) => ids.add(b.id));
    res.history.forEach((b) => ids.add(b.id));
    knownIdsRef.current = ids;
    return res.available;
  }, [isAuthenticated]);

  // Cargar al iniciar sesión y limpiar al cerrarla.
  useEffect(() => {
    if (!isAuthenticated) {
      genRef.current += 1;
      setAvailable([]);
      setHistory([]);
      setLoaded(false);
      setLoading(false);
      setCelebration(null);
      knownIdsRef.current = null;
      return;
    }
    refresh();
  }, [isAuthenticated, user?.id, refresh]);

  // Refresco suave al volver a la pestaña (p. ej. tras ganar un bono por push/correo).
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const onFocus = () => {
      if (Date.now() - lastFetchAt.current >= FOCUS_REFRESH_DEBOUNCE_MS) refresh();
    };
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [isAuthenticated, refresh]);

  /**
   * Tras confirmarse una recarga: vuelve a pedir los bonos y, si apareció un bono
   * de bienvenida que no conocíamos, abre el modal de celebración.
   */
  const detectNewWelcomeBonus = useCallback(async () => {
    if (!isAuthenticated) return null;
    const baseline = knownIdsRef.current ? new Set(knownIdsRef.current) : null;
    const findNew = (list) =>
      (list || []).find((b) => {
        if (b.source !== "WELCOME") return false;
        if (baseline) return !baseline.has(b.id);
        const issued = new Date(b.issued_at || 0).getTime();
        return Date.now() - issued < RECENT_BONUS_MS;
      }) || null;

    // El bono se emite al aprobar la recarga; un reintento corto cubre la latencia.
    let found = findNew(await refresh());
    if (!found) {
      await wait(4000);
      found = findNew(await refresh());
    }
    if (found) setCelebration(found);
    return found;
  }, [isAuthenticated, refresh]);

  const closeCelebration = useCallback(() => setCelebration(null), []);

  return (
    <BonusContext.Provider
      value={{
        available,
        history,
        availableCount: available.length,
        loaded,
        loading,
        refresh,
        detectNewWelcomeBonus,
        celebration,
        closeCelebration,
      }}
    >
      {children}
    </BonusContext.Provider>
  );
};

const EMPTY_BONUSES = {
  available: [],
  history: [],
  availableCount: 0,
  loaded: false,
  loading: false,
  refresh: async () => [],
  detectNewWelcomeBonus: async () => null,
  celebration: null,
  closeCelebration: () => {},
};

/** Bonos del usuario; fuera del provider devuelve un estado vacío (no rompe). */
export const useBonuses = () => useContext(BonusContext) || EMPTY_BONUSES;
